const ApiEndPoints = {
  AUTH: {
    OTP_REQUEST: "/api/v1/auth/otp/request",
    OTP_RESEND: "/api/v1/auth/otp/resend",
    OTP_VERIFY: "/api/v1/auth/otp/verify",
    REGISTER_COMPLETE: "/api/v1/auth/register/complete",
    PASSWORD_LOGIN: "/api/v1/auth/login/password",
    PASSWORD_FORGOT: "/api/v1/auth/password/forgot",
    PASSWORD_VERIFY: "/api/v1/auth/password/verify",
    PASSWORD_RESET: "/api/v1/auth/password/reset",
    REFRESH_TOKEN: "/api/v1/auth/refresh",
    LOGOUT: "/api/v1/auth/logout",
    IMPERSONATION_EXCHANGE: "/api/v1/auth/impersonation/exchange",
    SESSION_REVOKE: (id: string) => `/api/v1/auth/sessions/${id}/revoke`,
  },
  SESSIONS: {
    LIST: "/api/v1/me/sessions",
    REVOKE: (id: string) => `/api/v1/me/sessions/${id}/revoke`,
    REVOKE_OTHERS: "/api/v1/me/sessions/revoke-others",
  },
  ME: {
    PROFILE: "/api/v1/me/profile",
    AVATAR: "/api/v1/me/profile/avatar",
    CHANGE_PASSWORD: "/api/v1/me/profile/password",
    EMAIL_REQUEST: "/api/v1/me/email/request",
    EMAIL_VERIFY: "/api/v1/me/email/verify",
    EMAIL_SIMULATOR: "/api/v1/me/email/simulator",
    TWO_FACTOR_SETUP: "/api/v1/me/two-factor/setup",
    TWO_FACTOR_CONFIRM: "/api/v1/me/two-factor/confirm",
    TWO_FACTOR_DISABLE: "/api/v1/me/two-factor/disable",
    ACCOUNT_EXPORT: "/api/v1/me/account/export",
    ACCOUNT: "/api/v1/me/account",
    NOTIFICATIONS: "/api/v1/me/notifications",
    NOTIFICATION_UNREAD_COUNT: "/api/v1/me/notifications/unread-count",
    NOTIFICATION_MARK_ALL_READ: "/api/v1/me/notifications/mark-all-read",
    NOTIFICATION_READ: (id: string) => `/api/v1/me/notifications/${id}/read`,
    NOTIFICATION_ARCHIVE: (id: string) => `/api/v1/me/notifications/${id}/archive`,
    NOTIFICATION_UNARCHIVE: (id: string) => `/api/v1/me/notifications/${id}/unarchive`,
    NOTIFICATION_TOGGLE_PIN: (id: string) => `/api/v1/me/notifications/${id}/toggle-pin`,
    NOTIFICATION_DELETE: (id: string) => `/api/v1/me/notifications/${id}`,
  },
  TENANTS: {
    LIST: "/api/v1/me/tenants",
    BUSINESS_TYPES: "/api/v1/business-types",
    CITIES: "/api/v1/cities",
    CREATE: "/api/v1/tenants",
    UPDATE: (id: string) => `/api/v1/tenants/${id}`,
    DELETE: (id: string) => `/api/v1/tenants/${id}`,
    LOGO: (id: string) => `/api/v1/tenants/${id}/logo`,
    CHANNEL_ONBOARDING: (id: string) => `/api/v1/tenants/${id}/onboarding/channels`,
    ASSISTANT_ONBOARDING: (id: string) => `/api/v1/tenants/${id}/onboarding/assistant`,
    CHANNELS: (id: string) => `/api/v1/tenants/${id}/channels`,
    CHANNEL: (id: string, channel: string) => `/api/v1/tenants/${id}/channels/${channel}`,
    INSTAGRAM_AUTHORIZE: (id: string) => `/api/v1/tenants/${id}/channels/instagram/authorize`,
  },
  ASSISTANT: {
    CONFIG: (tenantId: string) => `/api/v1/tenants/${tenantId}/assistant`,
    COMPLIANCE_CHECK: (tenantId: string) =>
      `/api/v1/tenants/${tenantId}/assistant/compliance-check`,
    VERSIONS: (tenantId: string) => `/api/v1/tenants/${tenantId}/assistant/versions`,
    VERSION: (tenantId: string, versionId: string) =>
      `/api/v1/tenants/${tenantId}/assistant/versions/${versionId}`,
    VERSION_REVERT: (tenantId: string, versionId: string) =>
      `/api/v1/tenants/${tenantId}/assistant/versions/${versionId}/revert`,
    KNOWLEDGE: (tenantId: string) => `/api/v1/tenants/${tenantId}/assistant/knowledge`,
    KNOWLEDGE_SOURCE: (tenantId: string, sourceId: string) =>
      `/api/v1/tenants/${tenantId}/assistant/knowledge/${sourceId}`,
    KNOWLEDGE_REINDEX: (tenantId: string, sourceId: string) =>
      `/api/v1/tenants/${tenantId}/assistant/knowledge/${sourceId}/reindex`,
  },
  WIDGET: {
    CONFIG: (tenantId: string) => `/api/v1/tenants/${tenantId}/assistant/widget`,
    KEY_ROTATE: (tenantId: string) => `/api/v1/tenants/${tenantId}/assistant/widget/key/rotate`,
    KEY_REVOKE: (tenantId: string) => `/api/v1/tenants/${tenantId}/assistant/widget/key/revoke`,
  },
  CONVERSATIONS: {
    LIST: "/api/v1/conversations",
    SHOW: (id: string) => `/api/v1/conversations/${id}`,
  },
  INBOX: {
    CONVERSATIONS: (tenantId: string) => `/api/v1/tenants/${tenantId}/inbox/conversations`,
    COUNTERS: (tenantId: string) => `/api/v1/tenants/${tenantId}/inbox/conversations/counters`,
    BULK: (tenantId: string) => `/api/v1/tenants/${tenantId}/inbox/conversations/bulk`,
    CONVERSATION: (tenantId: string, id: string) =>
      `/api/v1/tenants/${tenantId}/inbox/conversations/${id}`,
    MESSAGES: (tenantId: string, conversationId: string) =>
      `/api/v1/tenants/${tenantId}/inbox/conversations/${conversationId}/messages`,
    UPLOAD: (tenantId: string) => `/api/v1/tenants/${tenantId}/inbox/uploads`,
    NOTES: (tenantId: string, conversationId: string) =>
      `/api/v1/tenants/${tenantId}/inbox/conversations/${conversationId}/notes`,
    NOTE: (tenantId: string, conversationId: string, noteId: string) =>
      `/api/v1/tenants/${tenantId}/inbox/conversations/${conversationId}/notes/${noteId}`,
    TAKEOVER: (tenantId: string, conversationId: string) =>
      `/api/v1/tenants/${tenantId}/inbox/conversations/${conversationId}/takeover`,
    HANDBACK: (tenantId: string, conversationId: string) =>
      `/api/v1/tenants/${tenantId}/inbox/conversations/${conversationId}/handback`,
    READ: (tenantId: string, conversationId: string) =>
      `/api/v1/tenants/${tenantId}/inbox/conversations/${conversationId}/read`,
    TYPING: (tenantId: string, conversationId: string) =>
      `/api/v1/tenants/${tenantId}/inbox/conversations/${conversationId}/typing`,
    REACTION: (tenantId: string, messageId: string) =>
      `/api/v1/tenants/${tenantId}/inbox/messages/${messageId}/reactions`,
    AI_ASSIST: (tenantId: string) => `/api/v1/tenants/${tenantId}/inbox/ai/assist`,
    AI_CORRECTIONS: (tenantId: string) => `/api/v1/tenants/${tenantId}/inbox/ai/corrections`,
    VIEWS: (tenantId: string) => `/api/v1/tenants/${tenantId}/inbox/views`,
    VIEW: (tenantId: string, viewId: string) => `/api/v1/tenants/${tenantId}/inbox/views/${viewId}`,
    SYNC: (tenantId: string) => `/api/v1/tenants/${tenantId}/inbox/sync`,
  },
  MESSAGES: {
    LIST: (conversationId: string) => `/api/v1/conversations/${conversationId}/messages`,
    SEND: (conversationId: string) => `/api/v1/conversations/${conversationId}/messages`,
  },
  MONITORING: {
    FRONTEND_ERRORS: "/api/v1/monitoring/frontend-errors",
  },
  SMS: {
    STATUS: "/api/v1/admin/sms/status",
    HISTORY: "/api/v1/admin/sms/history",
    SEND_TEST: "/api/v1/admin/sms/send-test",
    VOICE_CALL: "/api/v1/admin/sms/voice-call",
    TEMPLATES: "/api/v1/admin/sms-templates",
    TEMPLATE: (id: number) => `/api/v1/admin/sms-templates/${id}`,
  },
  ADMIN: {
    ME: "/api/v1/admin/me",
    PERMISSIONS: "/api/v1/admin/permissions",
    ROLES: "/api/v1/admin/roles",
    ROLE: (id: string) => `/api/v1/admin/roles/${id}`,
    STAFF: "/api/v1/admin/staff",
    STAFF_MEMBER: (id: string) => `/api/v1/admin/staff/${id}`,
    STAFF_STATS: "/api/v1/admin/staff/stats",
    STAFF_HISTORY: "/api/v1/admin/staff/history",
    USERS: "/api/v1/admin/users",
    USERS_EXPORT: "/api/v1/admin/users/export",
    USERS_DELETED: "/api/v1/admin/users/deleted",
    USER_DELETED: (id: string) => `/api/v1/admin/users/deleted/${id}`,
    USER_DELETED_RESTORE: (id: string) => `/api/v1/admin/users/deleted/${id}/restore`,
    USER: (id: string) => `/api/v1/admin/users/${id}`,
    USER_SESSIONS: (id: string) => `/api/v1/admin/users/${id}/sessions`,
    USER_SESSIONS_REVOKE: (id: string) => `/api/v1/admin/users/${id}/sessions/revoke`,
    USER_NOTIFICATIONS: (id: string) => `/api/v1/admin/users/${id}/notifications`,
    USER_ACCOUNT_HISTORY: (id: string) => `/api/v1/admin/users/${id}/account-history`,
    USER_ROLE_HISTORY: (id: string) => `/api/v1/admin/users/${id}/role-history`,
    USER_PASSWORD_RESET: (id: string) => `/api/v1/admin/users/${id}/password-reset`,
    USER_IMPERSONATION_TOKEN: (id: string) => `/api/v1/admin/users/${id}/impersonation-token`,
    USER_NOTIFY: (id: string) => `/api/v1/admin/users/${id}/notify`,
    USER_STATS: "/api/v1/admin/users/stats",
    TENANTS: "/api/v1/admin/tenants",
    TENANT: (id: string) => `/api/v1/admin/tenants/${id}`,
    TENANTS_SUSPENDED: "/api/v1/admin/tenants/suspended",
    TENANT_SUSPEND: (id: string) => `/api/v1/admin/tenants/${id}/suspend`,
    TENANT_RESTORE: (id: string) => `/api/v1/admin/tenants/suspended/${id}/restore`,
    TENANT_ASSISTANT: (id: string) => `/api/v1/admin/tenants/${id}/assistant`,
    TENANT_AUTO_REPLY: (tenantId: string, id: string) =>
      `/api/v1/admin/tenants/${tenantId}/auto-replies/${id}`,
    TENANT_BIZ_PAGE: (id: string) => `/api/v1/admin/tenants/${id}/biz-page`,
    TENANT_WIDGET: (id: string) => `/api/v1/admin/tenants/${id}/widget`,
    TEAMS: "/api/v1/admin/teams",
    TEAMS_STATS: "/api/v1/admin/teams/stats",
    MEMBERSHIPS: "/api/v1/admin/memberships",
    MEMBERSHIPS_STATS: "/api/v1/admin/memberships/stats",
    WIDGETS: "/api/v1/admin/widgets",
    WIDGET_REVOKE: (key: string) => `/api/v1/admin/widgets/${key}/revoke`,
    NOTIFICATIONS: "/api/v1/admin/notifications",
    NOTIFICATION_UNREAD_COUNT: "/api/v1/admin/notifications/unread-count",
    NOTIFICATION_MARK_ALL_READ: "/api/v1/admin/notifications/mark-all-read",
    NOTIFICATION_READ: (id: string) => `/api/v1/admin/notifications/${id}/read`,
  },
  BILLING: {
    CATALOG: "/api/v1/billing/catalog",
    PAYMENT_GATEWAYS: "/api/v1/billing/gateways",
    QUOTE: "/api/v1/billing/quote",
    QUOTE_INVOICE: "/api/v1/billing/quote/invoice",
    VALIDATE_DISCOUNT: "/api/v1/billing/discounts/validate",
    CHECKOUT: "/api/v1/billing/checkout",
    ORDER: (id: string) => `/api/v1/billing/orders/${id}`,
    ORDER_VERIFY: (id: string) => `/api/v1/billing/orders/${id}/verify`,
    ORDER_FAIL: (id: string) => `/api/v1/billing/orders/${id}/fail`,
    ORDER_RECEIPT: (id: string) => `/api/v1/billing/orders/${id}/receipt`,
    TRIAL_START: "/api/v1/billing/trial/start",
  },
  ME_BILLING: {
    SUMMARY: "/api/v1/me/billing",
    SUBSCRIPTIONS: "/api/v1/me/subscriptions",
    ORDERS: "/api/v1/me/orders",
    INVOICES: "/api/v1/me/invoices",
    WALLET_ENTRIES: "/api/v1/me/wallet/entries",
    // WALLET_WITHDRAWS: "/api/v1/me/wallet/withdraws",
    WALLET_TRANSFERS: "/api/v1/me/wallet/transfers",
    WALLET_TRANSFER_DESTINATIONS: "/api/v1/me/wallet/transfer-destinations",
    WALLET_CAPS: "/api/v1/me/wallet/caps",
    WALLET_AUTO_TOPUP: "/api/v1/me/wallet/auto-topup",
    WALLET_TOPUPS: "/api/v1/me/wallet/topups",
    WALLET_TOPUP: (id: string) => `/api/v1/me/wallet/topups/${id}`,
    WALLET_TOPUP_VERIFY: (id: string) => `/api/v1/me/wallet/topups/${id}/verify`,
    WALLET_TOPUP_FAIL: (id: string) => `/api/v1/me/wallet/topups/${id}/fail`,
    WALLET_AGGREGATES: "/api/v1/me/wallet/aggregates",
  },
  ADMIN_BILLING: {
    PLAN_STATS: "/api/v1/admin/billing/plans/stats",
    PLANS: "/api/v1/admin/billing/plans",
    PLAN: (key: string) => `/api/v1/admin/billing/plans/${key}`,
    PLAN_ARCHIVE: (key: string) => `/api/v1/admin/billing/plans/${key}/archive`,
    PLAN_RESTORE: (key: string) => `/api/v1/admin/billing/plans/${key}/restore`,
    PLAN_REORDER: "/api/v1/admin/billing/plans/reorder",
    REVISIONS: "/api/v1/admin/billing/revisions",
    PACKAGES: "/api/v1/admin/billing/packages",
    PACKAGE: (key: string) => `/api/v1/admin/billing/packages/${key}`,
    PACKAGE_ARCHIVE: (key: string) => `/api/v1/admin/billing/packages/${key}/archive`,
    PACKAGE_RESTORE: (key: string) => `/api/v1/admin/billing/packages/${key}/restore`,
    DISCOUNTS: "/api/v1/admin/billing/discounts",
    DISCOUNT: (code: string) => `/api/v1/admin/billing/discounts/${code}`,
    DISCOUNT_TOGGLE: (code: string) => `/api/v1/admin/billing/discounts/${code}/toggle`,
    TRANSACTIONS: "/api/v1/admin/billing/transactions",
        TRANSACTION_REFUND: (id: string) =>
          `/api/v1/admin/billing/transactions/${id}/refund`,
    WALLETS: "/api/v1/admin/billing/wallets",
    WALLET_TOTALS: "/api/v1/admin/billing/wallets/totals",
    WALLET_ENTRIES: (id: string) => `/api/v1/admin/billing/wallets/${id}/entries`,
    WALLET_ENTRIES_ALL: "/api/v1/admin/billing/wallets/entries",
    WALLET_ENTRY_REFUND: (id: string) =>
      `/api/v1/admin/billing/wallets/entries/${id}/refund`,
    WALLET_LATEST_ENTRIES: "/api/v1/admin/billing/wallet-entries",
    WALLET_CHARGE: "/api/v1/admin/billing/wallets/charge",
    WALLET_FREEZE: (id: string) => `/api/v1/admin/billing/wallets/${id}/freeze`,
    WALLET_UNFREEZE: (id: string) => `/api/v1/admin/billing/wallets/${id}/unfreeze`,
    WALLET_WITHDRAWS: "/api/v1/admin/billing/wallet-withdraws",
    WALLET_WITHDRAW_APPROVE: (id: string) => `/api/v1/admin/billing/wallet-withdraws/${id}/approve`,
    WALLET_WITHDRAW_REJECT: (id: string) => `/api/v1/admin/billing/wallet-withdraws/${id}/reject`,
    WALLET_WITHDRAW_PAID: (id: string) => `/api/v1/admin/billing/wallet-withdraws/${id}/paid`,
    SUBSCRIPTIONS: "/api/v1/admin/billing/subscriptions",
    SUBSCRIPTION_EXTEND: (id: string) => `/api/v1/admin/billing/subscriptions/${id}/extend`,
    SUBSCRIPTION_AUTO_RENEW: (id: string) =>
      `/api/v1/admin/billing/subscriptions/${id}/auto-renew`,
    SUBSCRIPTION_HISTORY: "/api/v1/admin/billing/subscriptions/history",
    REVENUE: "/api/v1/admin/billing/revenue",
    GATEWAYS: "/api/v1/admin/billing/gateways",
  },
  AUTO_REPLY: {
    LIST: (tenantId: string) => `/api/v1/tenants/${tenantId}/auto-replies`,
    CREATE: (tenantId: string) => `/api/v1/tenants/${tenantId}/auto-replies`,
    BULK: (tenantId: string) => `/api/v1/tenants/${tenantId}/auto-replies/bulk`,
    UPLOAD: (tenantId: string) => `/api/v1/tenants/${tenantId}/auto-replies/uploads`,
    SETTINGS: (tenantId: string) => `/api/v1/tenants/${tenantId}/auto-replies/settings`,
    ANALYTICS_OVERVIEW: (tenantId: string) =>
      `/api/v1/tenants/${tenantId}/auto-replies/analytics/overview`,
    ANALYTICS_HEATMAP: (tenantId: string) =>
      `/api/v1/tenants/${tenantId}/auto-replies/analytics/heatmap`,
    SHOW: (tenantId: string, id: string) => `/api/v1/tenants/${tenantId}/auto-replies/${id}`,
    UPDATE: (tenantId: string, id: string) => `/api/v1/tenants/${tenantId}/auto-replies/${id}`,
    DELETE: (tenantId: string, id: string) => `/api/v1/tenants/${tenantId}/auto-replies/${id}`,
    TOGGLE: (tenantId: string, id: string) =>
      `/api/v1/tenants/${tenantId}/auto-replies/${id}/toggle`,
    DUPLICATE: (tenantId: string, id: string) =>
      `/api/v1/tenants/${tenantId}/auto-replies/${id}/duplicate`,
    ANALYTICS_ITEM: (tenantId: string, id: string) =>
      `/api/v1/tenants/${tenantId}/auto-replies/${id}/analytics`,
  },
  CATALOG: {
    LIST: (tenantId: string) => `/api/v1/tenants/${tenantId}/products`,
    CREATE: (tenantId: string) => `/api/v1/tenants/${tenantId}/products`,
    BULK: (tenantId: string) => `/api/v1/tenants/${tenantId}/products/bulk`,
    UPLOAD: (tenantId: string) => `/api/v1/tenants/${tenantId}/products/uploads`,
    ANALYTICS: (tenantId: string) => `/api/v1/tenants/${tenantId}/products/analytics`,
    TAGS: (tenantId: string) => `/api/v1/tenants/${tenantId}/products/tags`,
    TAG: (tenantId: string, id: string) => `/api/v1/tenants/${tenantId}/products/tags/${id}`,
    SOURCES: (tenantId: string) => `/api/v1/tenants/${tenantId}/products/sources`,
    SOURCE_SYNC: (tenantId: string, source: string) =>
      `/api/v1/tenants/${tenantId}/products/sources/${source}/sync`,
    SOURCE_DISCONNECT: (tenantId: string, source: string) =>
      `/api/v1/tenants/${tenantId}/products/sources/${source}/disconnect`,
    TOKENS: (tenantId: string) => `/api/v1/tenants/${tenantId}/products/tokens`,
    TOKEN_REVOKE: (tenantId: string, id: string) =>
      `/api/v1/tenants/${tenantId}/products/tokens/${id}/revoke`,
    SHOW: (tenantId: string, id: string) => `/api/v1/tenants/${tenantId}/products/${id}`,
    UPDATE: (tenantId: string, id: string) => `/api/v1/tenants/${tenantId}/products/${id}`,
    DELETE: (tenantId: string, id: string) => `/api/v1/tenants/${tenantId}/products/${id}`,
    DUPLICATE: (tenantId: string, id: string) =>
      `/api/v1/tenants/${tenantId}/products/${id}/duplicate`,
    TOGGLE_AI: (tenantId: string, id: string) =>
      `/api/v1/tenants/${tenantId}/products/${id}/toggle-ai`,
  },
  BIZ_PAGE: {
    SETTINGS: (tenantId: string) => `/api/v1/tenants/${tenantId}/biz-page`,
    PUBLISH: (tenantId: string) => `/api/v1/tenants/${tenantId}/biz-page/publish`,
    SLUG_CHECK: (tenantId: string) => `/api/v1/tenants/${tenantId}/biz-page/slug-check`,
    PUBLIC: (slug: string) => `/api/v1/public/biz/${slug}`,
  },
  NOTIFICATION_TEMPLATES: "/api/v1/admin/notification-templates",
  NOTIFICATION_TEMPLATE: (id: number) => `/api/v1/admin/notification-templates/${id}`,
  NOTIFICATION_LOGS: "/api/v1/admin/notification-logs",
  NOTIFICATION_LOGS_STATS: "/api/v1/admin/notification-logs/stats",
  QUEUE_STATUS: "/api/v1/admin/queue/status",
  DEVELOPER: {
    API_KEYS: (tenantId: string) => `/api/v1/tenants/${tenantId}/developer/api-keys`,
    API_KEY_REVOKE: (tenantId: string, id: string) =>
      `/api/v1/tenants/${tenantId}/developer/api-keys/${id}/revoke`,
    WEBHOOKS: (tenantId: string) => `/api/v1/tenants/${tenantId}/developer/webhooks`,
    WEBHOOK: (tenantId: string, id: string) =>
      `/api/v1/tenants/${tenantId}/developer/webhooks/${id}`,
    WEBHOOK_TOGGLE: (tenantId: string, id: string) =>
      `/api/v1/tenants/${tenantId}/developer/webhooks/${id}/toggle`,
    WEBHOOK_DELIVERIES: (tenantId: string, id: string) =>
      `/api/v1/tenants/${tenantId}/developer/webhooks/${id}/deliveries`,
    LOGS: (tenantId: string) => `/api/v1/tenants/${tenantId}/developer/logs`,
  },
} as const;

export default ApiEndPoints;
